import Form from "antd/es/form";
import type { FormItemProps, FormInstance, FormProps, FormListProps, FormListFieldData, FormListOperation } from "antd/es/form";
import { ScrollFocusOptions } from "antd/es/form/Form";
import { NamePath } from "antd/es/form/interface";
import type { ReactNode } from "react";

export type { FormItemProps, FormInstance, FormProps, FormListProps, FormListFieldData, FormListOperation, ScrollFocusOptions, NamePath };

export const FItem = <T extends Record<string, any> = any>(props: FormItemProps<T>) => {
  return <Form.Item<T> {...props} className="!mb-0" />;
};

type CommonFormProps<T extends object = object> = {
  id?: string;
  form?: FormInstance<T>;
  children: ReactNode;
  scrollToFirstError?: boolean | ScrollFocusOptions | undefined;
  requiredMark?: FormProps<T>["requiredMark"];
  onFinish?: FormProps<T>["onFinish"];
  onFinishFailed?: FormProps<T>["onFinishFailed"];
  onValuesChange?: FormProps<T>["onValuesChange"];
  onFieldsChange?: FormProps<T>["onFieldsChange"];

  layout?: FormProps<T>["layout"];
  className?: string;
  disabled?: boolean;
  initialValues?: FormProps<T>["initialValues"];
  preserve?: FormProps<T>["preserve"];
  autoComplete?: string;
  component?: FormProps<T>["component"];
  
  formProps?: Omit<
    FormProps<T>,
    | "form"
    | "children"
    | "onFinish"
    | "onFinishFailed"
    | "onValuesChange"
    | "onFieldsChange"
    | "layout"
    | "className"
    | "disabled"
    | "initialValues"
    | "preserve"
  >;
};

export const CommonForm = <T extends object = object>({
  id,
  form,
  children,
  requiredMark,
  onFinish,
  onFinishFailed,
  onValuesChange,
  onFieldsChange,
  scrollToFirstError,
  layout = "vertical",
  className = "h-full min-w-0 overflow-hidden",
  disabled = false,
  initialValues,
  preserve,
  formProps,
  autoComplete,
  component
}: CommonFormProps<T>) => {
  return (
    <Form<T>
      {...formProps}
      id={id}
      form={form}
      layout={layout}
      className={className}
      disabled={disabled}
      initialValues={initialValues}
      preserve={preserve}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      onValuesChange={onValuesChange}
      onFieldsChange={onFieldsChange}
      scrollToFirstError={scrollToFirstError}
      requiredMark={requiredMark}
      autoComplete={autoComplete}
      component={component}
    >
      {children}
    </Form>
  );
};

type CommonFormListProps = Omit<FormListProps, "children"> & {
  children: (fields: FormListFieldData[], operation: FormListOperation, meta: { errors: ReactNode[] }) => ReactNode;
};

export const CommonFormList = ({ name, children, ...rest }: CommonFormListProps) => {
  return (
    <Form.List name={name} {...rest}>
      {(fields, operation, meta) => children(fields, operation, meta)}
    </Form.List>
  );
};

export const useCommonForm = <T extends Record<string, any> = any>() => {
  const [form] = Form.useForm<T>();
  return form;
};

export const useCommonFormInstance = <T extends Record<string, any> = any>() => {
  const form = Form.useFormInstance<T>();
  return form as FormInstance<T>;
};

export const useCommonWatch = <T = any>(name: NamePath, formInstance?: FormInstance) => {
  return Form.useWatch<T>(name, formInstance);
}
